import { WorkPage } from '../components/work-page.tsx';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCreationView } from '../hooks/use-creation-view.ts';
import { invalidateQuoteViews } from '../state/quote-cache.ts';
import { useTranslation } from 'react-i18next';
import { customerApi, quoteApi } from '../api/endpoints.ts';
import { ApiRequestError } from '../api/client.ts';
import { useSession } from '../state/session.tsx';
import { Alert, Button, classes, ConfirmDialog, LoadingBlock, Select, TextInput } from '../components/ui.tsx';
import { useDocumentTitle, useStoredPreference } from '../hooks/index.ts';
import CustomerForm from '../forms/customer-form.tsx';
import PropertyForm from '../forms/property-form.tsx';
import QuickQuote, { type QuickQuoteInputState, type QuickQuoteRecoveryIds } from '../components/quick-quote.tsx';
import {
  MagicQuoteStartPanel,
  QUOTE_METHODS,
  QuoteMethodPicker,
  isQuoteMethod,
  useQuoteMethods,
  type QuoteMethod,
} from '../components/quote/quote-methods.tsx';
import { clearActiveQuickQuote, clearPendingQuickQuote } from '../state/quick-quote-recovery.ts';
import { COMMERCIAL_PROPERTY_TYPES, QUOTE_SEGMENTS, type QuoteSegment } from '@shared/constants/index.ts';
import type { Customer, Property } from '@shared/types/index.ts';

/**
 * The one place a quote starts, whichever of the three ways it is about to be
 * made: a Magic Quote walkthrough, a Quick Quote paragraph, or the editor by
 * hand. The method is chosen first, then the customer and property that all
 * three share (spec §16.1 steps 1 and 2), then the chosen method's own panel.
 *
 * The quote itself is created once both are known, so a half-finished quote
 * never lands in the list — and a walkthrough, which needs neither, goes
 * straight to the capture screen.
 *
 * `?method=` picks the method for a link that knows what it wants (the
 * Walkthroughs tab's "Start walkthrough", a review screen's "build it by hand
 * instead"), and the last method used is remembered on this browser for the
 * cleaner who always walks and the office that always types.
 */
type LeaveKind = 'manual' | 'cancel' | 'switch' | 'context' | 'stuck';

export default function NewQuotePage() {
  const { t } = useTranslation();
  useDocumentTitle(t('newQuote.documentTitle'));
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { company, currentLocation, can } = useSession();
  const requestedCustomerId = searchParams.get('customerId');
  const requestedPropertyId = searchParams.get('propertyId');
  const prefillApplied = useRef(false);

  const methods = useQuoteMethods();
  const available = methods.filter((option) => option.available).map((option) => option.method);
  const [rememberedMethod, rememberMethod] = useStoredPreference('new-quote-method', QUOTE_METHODS, 'quick');
  /**
   * The URL wins, then memory, then the first thing that is actually offered.
   * An unavailable method is never selected, whoever asked for it: a link to
   * `?method=magic` on a Starter plan lands on whatever this person can use,
   * beside a locked card that says why (for the owner) or nothing (for
   * everybody else).
   */
  const requestedMethod = searchParams.get('method');
  // Unfinished work outranks habit: a Quick Quote this browser has paid to
  // parse is shown before the method used last, or it would sit unseen behind
  // the editor until its pointer expired.
  const resumableMethod = methods.find((option) => option.resumable)?.method ?? null;
  const pick = (candidates: Array<string | null>): QuoteMethod =>
    candidates.find(
      (candidate): candidate is QuoteMethod => isQuoteMethod(candidate) && available.includes(candidate),
    ) ??
    available[0] ??
    'manual';
  const method = pick([requestedMethod, resumableMethod, rememberedMethod]);

  function chooseMethod(next: QuoteMethod) {
    // Changing tabs unmounts Quick Quote, so it is a way out like the others.
    if (next === 'quick') applyMethod(next);
    else leaveQuickQuote('switch', () => applyMethod(next));
  }

  function applyMethod(next: QuoteMethod) {
    rememberMethod(next);
    // Kept in the URL so a reload, or Back from the walkthrough, lands on the
    // same choice. Replaced rather than pushed: changing tabs on a form is
    // not a page somebody wants to step back through.
    const params = new URLSearchParams(searchParams);
    params.set('method', next);
    setSearchParams(params, { replace: true });
  }

  const [search, setSearch] = useState('');
  const [customer, setCustomer] = useState<Customer | null>(null);
  const [propertyId, setPropertyId] = useState<string>('');
  const [showCustomerForm, setShowCustomerForm] = useState(false);
  const [showPropertyForm, setShowPropertyForm] = useState(false);
  const [propertyDraftStarted, setPropertyDraftStarted] = useState(false);
  const propertyDraftStartedRef = useRef(false);
  const [awaitingFirstProperty, setAwaitingFirstProperty] = useState(false);
  const [segment, setSegment] = useState<QuoteSegment>('residential');
  const [error, setError] = useState<string | null>(null);
  /**
   * Whether the customer named in the URL still stands for a walkthrough
   * while their record has not been fetched. A link with `?customerId=` opened
   * in a driveway with no signal cannot load the record, and a walkthrough
   * started then must still carry the id — the old Magic Quote page read it
   * straight from the query string, and adoption later relies on it. Cleared
   * the moment somebody says "choose someone else": from then on nobody is
   * the answer, not the URL.
   */
  const [requestedCustomerDetached, setRequestedCustomerDetached] = useState(false);
  // A recovered Quick Quote owns its saved segment. Keep the ordinary
  // property-type suggestion from overwriting that explicit context.
  const [segmentPinned, setSegmentPinned] = useState(false);
  // Under Quick Quote the editor by hand is the way out, not the next step: a
  // paragraph or photos waiting to be drafted are asked about before any way
  // out drops them, and none is open while a draft is on its way.
  const [quickQuoteInput, setQuickQuoteInput] = useState<QuickQuoteInputState>('clear');
  // The pointers the mounted Quick Quote is waiting on, as it last reported.
  const quickQuoteRecovery = useRef<QuickQuoteRecoveryIds | null>(null);
  const reportQuickQuoteRecovery = useCallback((recovery: QuickQuoteRecoveryIds) => {
    quickQuoteRecovery.current = recovery;
  }, []);
  // Which way out is waiting on that question, and what it does once answered.
  const [leavingQuickQuote, setLeavingQuickQuote] = useState<{ kind: LeaveKind; run: () => void } | null>(null);
  const quickQuoteSubmitting = method === 'quick' && quickQuoteInput === 'submitting';
  // Set once Cancel has left: a quote still being generated must not pull the
  // person back to it when it finishes. It is still in the list.
  const leftQuickQuote = useRef(false);

  /**
   * Every control that unmounts Quick Quote goes through here: the editor by
   * hand, Cancel, another method, and a change of customer, address or kind
   * of work (each starts a new panel). Unsent input is asked about first.
   * While a request is in flight in this tab only Cancel leaves, without
   * asking: it settles in seconds, and starting anything else meanwhile could
   * make a second quote. A Quick Quote that is stuck — its last request's
   * outcome unknown, or its saved work not checkable — is asked about and
   * then let go: this device stops resuming it, so the next visit does not
   * land back in the same wait, and a draft it still produces later is in
   * the quotes list.
   */
  function leaveQuickQuote(kind: LeaveKind, run: () => void) {
    const go =
      kind === 'cancel'
        ? () => {
            leftQuickQuote.current = true;
            run();
          }
        : run;
    if (method === 'quick' && quickQuoteInput === 'unsent') {
      setLeavingQuickQuote({ kind, run: go });
      return;
    }
    if (quickQuoteSubmitting && kind !== 'cancel') return;
    if (method === 'quick' && quickQuoteInput === 'stuck' && kind !== 'cancel') {
      setLeavingQuickQuote({
        kind: 'stuck',
        run: () => {
          forgetQuickQuoteRecovery();
          go();
        },
      });
      return;
    }
    go();
  }

  /**
   * Stops this device resuming the Quick Quote it was waiting on — only its
   * own pointers, which the clear helpers leave alone if another tab has
   * since stored newer ones.
   */
  function forgetQuickQuoteRecovery() {
    const recovery = quickQuoteRecovery.current;
    if (!recoveryScope || !recovery) return;
    if (recovery.pendingCreateId) clearPendingQuickQuote(recoveryScope, recovery.pendingCreateId);
    if (recovery.activeSessionId) clearActiveQuickQuote(recoveryScope, recovery.activeSessionId);
  }

  const recoveryScope = useMemo(
    () => (company ? { companyId: company.id, locationId: currentLocation?.id ?? null } : null),
    [company, currentLocation?.id],
  );

  /**
   * The segment question only exists for a company that does both kinds of
   * work — anywhere else the Worker's default (the company's own mode) is
   * already the answer, so nothing is asked and nothing is sent.
   */
  const asksSegment = company?.serviceMode === 'both';
  const effectiveSegment: QuoteSegment = asksSegment
    ? segment
    : company?.serviceMode === 'commercial'
      ? 'commercial'
      : 'residential';

  const requestedCustomer = useQuery({
    queryKey: ['customer', 'new-quote-prefill', company?.id, currentLocation?.id ?? 'company', requestedCustomerId],
    queryFn: () => customerApi.get(requestedCustomerId!),
    enabled: Boolean(requestedCustomerId) && !prefillApplied.current,
    retry: false,
  });

  const customers = useQuery({
    queryKey: ['customers', { search }],
    queryFn: () => customerApi.list({ q: search, pageSize: 20 }),
    enabled: !customer,
  });

  const properties = useQuery({
    queryKey: ['customer-properties', customer?.id],
    queryFn: () => customerApi.properties(customer!.id),
    enabled: Boolean(customer),
  });

  const propertyContextSettled =
    !propertyId || Boolean(properties.data?.items.some((property) => property.id === propertyId));

  useEffect(() => {
    if (prefillApplied.current || !requestedCustomer.data) return;
    if (customer) {
      prefillApplied.current = true;
      return;
    }
    prefillApplied.current = true;
    chooseCustomer(requestedCustomer.data);
    if (requestedPropertyId) setPropertyId(requestedPropertyId);
  }, [customer, requestedCustomer.data, requestedPropertyId]);

  useEffect(() => {
    if (prefillApplied.current || !requestedCustomer.isError || customer) return;
    prefillApplied.current = true;
    // A server that has settled the question — 404 for a deleted customer,
    // 403 for one in another branch — means a walkthrough must not keep
    // carrying an id every later start would be refused for. Anything else
    // keeps the URL's customer standing: a transport failure (no answer at
    // all, the offline case) or a passing 429 or 5xx says nothing about the
    // customer, only about the moment, and the start itself will resolve the
    // id once the service is back.
    if (
      requestedCustomer.error instanceof ApiRequestError &&
      (requestedCustomer.error.status === 404 || requestedCustomer.error.status === 403)
    ) {
      setRequestedCustomerDetached(true);
    }
    setError(
      requestedCustomer.error instanceof ApiRequestError
        ? requestedCustomer.error.message
        : t('quickQuote.prefillError'),
    );
  }, [customer, requestedCustomer.error, requestedCustomer.isError, t]);

  useEffect(() => {
    if (!requestedPropertyId || propertyId !== requestedPropertyId || !properties.data) return;
    if (properties.data.items.some((property) => property.id === requestedPropertyId)) return;
    setPropertyId('');
    setError(t('quickQuote.prefillPropertyError'));
  }, [properties.data, propertyId, requestedPropertyId, t]);

  const queryClient = useQueryClient();
  const captureCreationView = useCreationView([customer?.id, propertyId, effectiveSegment, method]);
  const capturePropertySaveView = useCreationView([customer?.id, showPropertyForm]);
  const createQuote = useMutation({
    mutationFn: ({ body, isCurrent }: { body: Parameters<typeof quoteApi.create>[0]; isCurrent: () => boolean }) => {
      // A queued mutation must not start under a later form or access context.
      if (!isCurrent()) throw new Error('The quote creation view changed before submission.');
      return quoteApi.create(body);
    },
    onSuccess: (quote, attempt) => {
      // The server kept the quote even when Cancel/Back dropped navigation.
      // Refresh list caches so the office does not create a duplicate.
      invalidateQuoteViews(queryClient);
      if (attempt.isCurrent()) navigate(`/quotes/${quote.id}`, { replace: true });
    },
    onError: (caught, attempt) => {
      if (attempt.isCurrent()) {
        setError(caught instanceof ApiRequestError ? caught.message : t('newQuote.createError'));
      }
    },
  });

  /**
   * Defaults the segment from the property that was just picked — an office
   * is almost always a commercial job, a house almost never — while leaving
   * the choice itself editable: the toggle below is the override.
   */
  const selectedProperty = properties.data?.items.find((property) => property.id === propertyId);
  const selectedPropertyType = selectedProperty?.propertyType;
  useEffect(() => {
    if (!selectedPropertyType || segmentPinned) return;
    setSegment(COMMERCIAL_PROPERTY_TYPES.includes(selectedPropertyType) ? 'commercial' : 'residential');
  }, [segmentPinned, selectedPropertyType]);

  /**
   * Picks step 2 for a customer who was just added here.
   *
   * A new customer can arrive with a property already attached — the billing
   * address, saved as one by the tick box on the customer form — so the choice
   * waits for that list to load. Opening the blank property form regardless
   * would ask the estimator to type the address a second time and leave the
   * customer holding two copies of it.
   *
   * A failed lookup releases the wait rather than holding the page hostage to
   * it: the picker and its "add property" button are both still there.
   */
  useEffect(() => {
    if (!awaitingFirstProperty) return;

    if (properties.isError) {
      setAwaitingFirstProperty(false);
      return;
    }

    const items = properties.data?.items;
    if (!items) return;

    setAwaitingFirstProperty(false);
    // A late automatic choice must not reset a draft the user explicitly
    // opened, edited or submitted while this lookup was pending.
    if (propertyDraftStartedRef.current) return;
    if (items[0]) setPropertyId(items[0].id);
    else openPropertyForm(false);
  }, [awaitingFirstProperty, properties.data, properties.isError]);

  /**
   * Step 1's only way to change who the quote is for, so the wait for a
   * just-created customer's property list is dropped here.
   *
   * Left standing, it would be satisfied by the *next* customer's list instead
   * — picking their first property, or opening a blank property form for them
   * — and the quote would start against an address nobody chose.
   */
  function chooseCustomer(chosen: Customer | null) {
    if (chosen === null) setRequestedCustomerDetached(true);
    setCustomer(chosen);
    setPropertyId('');
    setAwaitingFirstProperty(false);
    closePropertyForm();
    setSegmentPinned(false);
  }

  function selectProperty(property: Property) {
    setSegmentPinned(false);
    setPropertyId(property.id);
    closePropertyForm();
    void properties.refetch();
  }

  function openPropertyForm(started: boolean) {
    propertyDraftStartedRef.current = started;
    setPropertyDraftStarted(started);
    setShowPropertyForm(true);
  }

  function closePropertyForm() {
    propertyDraftStartedRef.current = false;
    setPropertyDraftStarted(false);
    setShowPropertyForm(false);
  }

  function markPropertyDraftStarted() {
    propertyDraftStartedRef.current = true;
    setPropertyDraftStarted(true);
  }

  function startManualQuote() {
    // An untouched automatic form is optional. Close only that blank step;
    // an input/save event holds creation immediately, including before React
    // paints the disabled button.
    if (propertyDraftStartedRef.current || !customer) return;
    closePropertyForm();
    createQuote.mutate({
      body: {
        customerId: customer.id,
        propertyId: propertyId || null,
        ...(asksSegment ? { segment } : {}),
      },
      isCurrent: captureCreationView(),
    });
  }

  return (
    <WorkPage title={t('newQuote.title')} description={t('newQuote.description')}>
      {error ? (
        <div className="mb-6">
          <Alert tone="error">{error}</Alert>
        </div>
      ) : null}

      {/* ---- How: which of the three ways ------------------------------- */}
      <QuoteMethodPicker
        options={methods}
        value={method}
        onChange={chooseMethod}
        onLeave={(go) => leaveQuickQuote('cancel', go)}
      />

      {/* ---- Step 1: customer ------------------------------------------- */}
      <section className="card mb-6" aria-labelledby="customer-heading">
        <h2 id="customer-heading" className={classes('text-lg font-semibold', method === 'magic' ? 'mb-1' : 'mb-4')}>
          {t('newQuote.customerHeading')}
        </h2>
        {/* A walkthrough is the one route that does not need to know who it
            is for yet; the other two create a quote, and a quote is a
            document addressed to somebody. */}
        {method === 'magic' ? (
          <p className="mb-4 text-sm text-[color:var(--color-muted)]">{t('newQuote.customerOptionalForWalkthrough')}</p>
        ) : null}

        {customer ? (
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <p className="font-semibold">{customer.displayName}</p>
              <p className="text-sm text-[color:var(--color-muted)]">
                {[customer.email, customer.phone].filter(Boolean).join(' · ') || t('newQuote.noContactDetails')}
              </p>
            </div>
            <Button
              disabled={quickQuoteSubmitting}
              onClick={() => leaveQuickQuote('context', () => chooseCustomer(null))}
            >
              {t('newQuote.chooseSomeoneElse')}
            </Button>
          </div>
        ) : showCustomerForm ? (
          <CustomerForm
            onSaved={(created) => {
              setCustomer(created);
              setShowCustomerForm(false);
              setAwaitingFirstProperty(true);
            }}
            onCancel={() => setShowCustomerForm(false)}
          />
        ) : (
          <>
            <TextInput
              label={t('newQuote.searchCustomersLabel')}
              type="search"
              placeholder={t('newQuote.searchCustomersPlaceholder')}
              value={search}
              onChange={(event) => setSearch(event.target.value)}
            />

            {customers.isLoading ? <LoadingBlock label={t('newQuote.searching')} /> : null}

            {customers.data && customers.data.items.length > 0 ? (
              <ul className="mb-4 divide-y divide-[color:var(--color-line)] rounded-md border border-[color:var(--color-line)]">
                {customers.data.items.map((item) => (
                  <li key={item.id}>
                    <button
                      type="button"
                      onClick={() => chooseCustomer(item)}
                      className="w-full px-4 py-3 text-left hover:bg-[color:var(--color-surface)]"
                    >
                      <span className="font-medium">{item.displayName}</span>
                      {item.email ? (
                        <span className="block text-sm text-[color:var(--color-muted)]">{item.email}</span>
                      ) : null}
                    </button>
                  </li>
                ))}
              </ul>
            ) : null}

            {customers.data && customers.data.items.length === 0 ? (
              <p className="mb-4 text-[color:var(--color-muted)]">{t('newQuote.noCustomersMatch')}</p>
            ) : null}

            <Button variant="primary" onClick={() => setShowCustomerForm(true)}>
              {t('newQuote.addNewCustomer')}
            </Button>
          </>
        )}
      </section>

      {/* ---- Step 2: property ------------------------------------------- */}
      {customer ? (
        <section className="card mb-6" aria-labelledby="property-heading">
          <h2 id="property-heading" className="mb-4 text-lg font-semibold">
            {t('newQuote.propertyHeading')}
          </h2>

          {properties.isFetching ? <LoadingBlock label={t('newQuote.loadingProperties')} /> : null}

          {properties.isError || !propertyContextSettled ? (
            <div className="mb-4">
              {properties.isError || !properties.isFetching ? (
                <Alert tone="error">
                  {t(properties.isError ? 'newQuote.propertiesLoadError' : 'newQuote.propertyUnconfirmed')}
                </Alert>
              ) : null}
              <Button className="mt-3" busy={properties.isFetching} onClick={() => void properties.refetch()}>
                {t('common.retry')}
              </Button>
            </div>
          ) : null}

          {showPropertyForm ? (
            <PropertyForm
              customerId={customer.id}
              onSaved={selectProperty}
              onCancel={closePropertyForm}
              captureSaveView={capturePropertySaveView}
              preventCancelWhileSaving
              onDraftStarted={markPropertyDraftStarted}
            />
          ) : (
            <>
              <Select
                label={t('newQuote.whichProperty')}
                hint={t('newQuote.whichPropertyHint')}
                value={propertyId}
                disabled={quickQuoteSubmitting}
                onChange={(event) => {
                  const next = event.target.value;
                  leaveQuickQuote('context', () => {
                    setSegmentPinned(false);
                    setPropertyId(next);
                  });
                }}
                options={[
                  { value: '', label: t('newQuote.notSpecifiedYet') },
                  // Keep an unverified prefill or newly saved ID visible
                  // while the list is unavailable, without treating it as
                  // validated for quote creation.
                  ...(!propertyContextSettled
                    ? [{ value: propertyId, label: t('newQuote.propertyConfirmationNeeded') }]
                    : []),
                  ...(properties.data?.items ?? []).map((property) => ({
                    value: property.id,
                    label:
                      property.name ??
                      [property.addressLine1, property.city].filter(Boolean).join(', ') ??
                      t('newQuote.unnamedProperty'),
                  })),
                ]}
              />
              <Button
                disabled={quickQuoteSubmitting}
                onClick={() => leaveQuickQuote('context', () => openPropertyForm(true))}
              >
                {t('newQuote.addProperty')}
              </Button>
            </>
          )}
        </section>
      ) : null}

      {/* ---- Which kind of work (companies that do both) ------------------ */}
      {/* Not asked for a walkthrough: the walk works out what kind of place
          this is, and the segment is settled with everything else on review. */}
      {asksSegment && customer && method !== 'magic' ? (
        <fieldset className="card mb-6">
          <legend className="mb-4 text-lg font-semibold">{t('newQuote.segmentHeading')}</legend>
          <div className="flex flex-wrap gap-3">
            {QUOTE_SEGMENTS.map((option) => (
              <label
                key={option}
                className={classes(
                  'flex cursor-pointer items-center gap-2 rounded-md border px-3 py-2',
                  segment === option
                    ? 'border-[color:var(--color-brand)] bg-[color:var(--color-brand-soft)]'
                    : 'border-[color:var(--color-line)]',
                )}
              >
                <input
                  type="radio"
                  name="quote-segment"
                  checked={segment === option}
                  disabled={quickQuoteSubmitting}
                  onChange={() =>
                    leaveQuickQuote('context', () => {
                      setSegmentPinned(true);
                      setSegment(option);
                    })
                  }
                />
                <span>{t(`newQuote.segment.${option}`)}</span>
              </label>
            ))}
          </div>
          <p className="field-hint mt-2">{t('newQuote.segmentHint')}</p>
        </fieldset>
      ) : null}

      {/* ---- The chosen method's own panel -------------------------------- */}
      {method === 'magic' ? (
        <MagicQuoteStartPanel
          // With no customer loaded, the URL's ids stand in — see
          // `requestedCustomerDetached`. Once one is loaded, only what is on
          // the page counts: a URL property the list turned out not to hold
          // has already been cleared above, and must not come back here.
          customerId={customer ? customer.id : requestedCustomerDetached ? null : requestedCustomerId}
          customerName={customer?.displayName ?? null}
          propertyId={customer ? propertyId || null : requestedCustomerDetached ? null : requestedPropertyId}
          propertyLabel={selectedProperty ? (selectedProperty.name ?? selectedProperty.addressLine1 ?? null) : null}
          // Same gap the manual button waits out: a customer just added, whose
          // first property is still being looked up and chosen.
          settling={awaitingFirstProperty}
        />
      ) : null}

      {method === 'quick' &&
      can('quotes:write') &&
      company &&
      recoveryScope &&
      customer &&
      !showPropertyForm &&
      !awaitingFirstProperty &&
      !properties.isLoading &&
      propertyContextSettled ? (
        <QuickQuote
          key={`${company.id}:${currentLocation?.id ?? 'company'}:${customer.id}:${propertyId || 'none'}:${effectiveSegment}`}
          customerId={customer.id}
          propertyId={propertyId || null}
          segment={effectiveSegment}
          recoveryScope={recoveryScope}
          manualQuoteHref="#manual-quote-builder"
          onInputStateChange={setQuickQuoteInput}
          onRecoveryChange={reportQuickQuoteRecovery}
          onGenerated={(quoteId) => {
            if (!leftQuickQuote.current) navigate(`/quotes/${quoteId}`, { replace: true });
          }}
          onResumeContext={async (next) => {
            if (company.serviceMode !== 'both' && next.segment !== effectiveSegment) {
              throw new Error(t('quickQuote.recovery.segmentUnavailable'));
            }
            const originalCustomer = await customerApi.get(next.customerId);
            if (next.propertyId) {
              const originalProperties = await customerApi.properties(next.customerId);
              if (!originalProperties.items.some((property) => property.id === next.propertyId)) {
                throw new Error(t('quickQuote.recovery.propertyUnavailable'));
              }
            }
            chooseCustomer(originalCustomer);
            setPropertyId(next.propertyId ?? '');
            setSegmentPinned(true);
            setSegment(next.segment);
          }}
        />
      ) : null}

      {/* The editor by hand. Also under Quick Quote, as the way out it has
          always offered (`manualQuoteHref` above points here): a paragraph
          that will not parse should not cost the customer and property
          already chosen. */}
      {method !== 'magic' ? (
        <div id="manual-quote-builder">
          {method === 'quick' && customer ? (
            <p className="mb-2 text-sm text-[color:var(--color-muted)]">{t('quickQuote.manualFallback')}</p>
          ) : null}

          {showPropertyForm ? (
            <p className="mb-3 text-sm text-[color:var(--color-muted)]">
              {t(propertyDraftStarted ? 'newQuote.finishPropertyFirst' : 'newQuote.whichPropertyHint')}
            </p>
          ) : null}

          <div className="flex flex-wrap gap-3">
            <Button
              // Under Quick Quote, "Draft rooms" is the one primary action;
              // a second green button below it is too easy to tap instead.
              variant={method === 'quick' ? 'secondary' : 'primary'}
              // Wait until a prefilled property is in the loaded list (and any
              // automatic segment flip from it has run) so submit cannot start
              // against a creation view that will be replaced mid-flight.
              disabled={!customer || !propertyContextSettled || propertyDraftStarted || quickQuoteSubmitting}
              // Busy also covers the moment step 2 is still being decided for a
              // customer who was just added: a click in that gap would build the
              // quote against no property at all and quietly drop the one their
              // billing address had already become.
              busy={createQuote.isPending || awaitingFirstProperty}
              onClick={() => leaveQuickQuote('manual', startManualQuote)}
            >
              {t(method === 'quick' ? 'quickQuote.manualAction' : 'newQuote.startBuilding')}
            </Button>
            <Button onClick={() => leaveQuickQuote('cancel', () => navigate('/quotes'))}>{t('common.cancel')}</Button>
          </div>
        </div>
      ) : null}

      {leavingQuickQuote ? (
        <ConfirmDialog
          title={t(`newQuote.leaveQuick.${leavingQuickQuote.kind}.title`)}
          confirmLabel={t(`newQuote.leaveQuick.${leavingQuickQuote.kind}.confirm`)}
          cancelLabel={t('newQuote.leaveQuick.keep')}
          confirmVariant={leavingQuickQuote.kind === 'cancel' ? 'danger' : 'primary'}
          initialFocus="cancel"
          onCancel={() => setLeavingQuickQuote(null)}
          onConfirm={() => {
            setLeavingQuickQuote(null);
            leavingQuickQuote.run();
          }}
        >
          <p>{t(`newQuote.leaveQuick.${leavingQuickQuote.kind}.body`)}</p>
          {leavingQuickQuote.kind !== 'stuck' ? <p className="mt-2">{t('newQuote.leaveQuick.photosKept')}</p> : null}
        </ConfirmDialog>
      ) : null}
    </WorkPage>
  );
}
